'use client';

import * as React from 'react';
import { Checkbox } from './checkbox';
import { Label } from './label';
import { cn } from '@/shared/utils/cn';

export interface CheckboxFieldProps {
  id: string;
  checked: boolean;
  onCheckedChange?: (checked: boolean) => void;
  /** Primary label text (or arbitrary node). */
  label: React.ReactNode;
  /** Optional secondary hint below the label. */
  hint?: React.ReactNode;
  disabled?: boolean;
  className?: string;
  /** Render the label muted + small (used for fine-print gates like the ToS). */
  finePrint?: boolean;
  'data-testid'?: string;
}

/**
 * One checkbox-with-label control. This is ONE control (the checkbox) plus its
 * `<label htmlFor>` — the accessible norm; a Radix checkbox renders a
 * `<button role="checkbox">` which can't be nested in a `<label>`, so the box
 * and label are siblings and the label is wired with `htmlFor`.
 *
 * Alignment follows shadcn's shipped Field pattern: `grid [auto_1fr]` +
 * `items-start` tops-aligns the box with the label's first line, a single 1px
 * optical nudge (`mt-px`) corrects box-cap-height vs text, and the hint sits in
 * `col-start-2` so it indents under the label past the box automatically — no
 * fixed heights, survives wrapping and font-size changes. The hint is linked
 * with `aria-describedby` so the label stays the concise accessible name.
 */
export function CheckboxField({
  id,
  checked,
  onCheckedChange,
  label,
  hint,
  disabled,
  className,
  finePrint,
  'data-testid': testId,
}: CheckboxFieldProps) {
  const hintId = hint ? `${id}-hint` : undefined;
  return (
    <div className={cn('grid grid-cols-[auto_1fr] items-start gap-x-2.5 gap-y-1', className)}>
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={onCheckedChange ? (c) => onCheckedChange(c === true) : undefined}
        disabled={disabled}
        aria-describedby={hintId}
        data-testid={testId}
        className="mt-px shrink-0"
      />
      <Label
        htmlFor={id}
        className={cn(
          'cursor-pointer leading-snug',
          finePrint ? 'text-xs font-normal text-muted-foreground' : 'text-sm font-medium',
          disabled && 'cursor-not-allowed opacity-70',
        )}
      >
        {label}
      </Label>
      {hint && (
        <p id={hintId} className="col-start-2 text-xs leading-snug text-muted-foreground">
          {hint}
        </p>
      )}
    </div>
  );
}
